<template>
  <div class="home-page">
    <!-- Hero Section -->
    <section class="hero-section" aria-labelledby="hero-title">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-6">
            <div class="hero-content fade-in-up">
              <h1 id="hero-title" class="hero-title">{{ $t('home.title') }}</h1>
              <p class="hero-description">{{ $t('home.description') }}</p>
              <div class="hero-buttons">
                <a 
                  href="https://apps.apple.com/app/id6747022496" 
                  target="_blank"
                  rel="noopener noreferrer"
                  class="download-btn apple-download me-3"
                  aria-label="Download on App Store"
                >
                  <img src="@/assets/app-store-badge.svg" alt="Download on App Store" loading="eager">
                </a>
                <div class="download-btn-wrapper">
                  <a 
                    href="javascript:void(0)" 
                    class="download-btn google-download disabled"
                    aria-label="Google Play - Coming Soon"
                    aria-disabled="true"
                  >
                    <img src="@/assets/google-play-badge.svg" alt="Get it on Google Play" loading="eager">
                  </a>
                  <span class="coming-soon-badge">{{ $t('home.comingSoon') }}</span>
                </div>
              </div>
              <ul class="env-pills" aria-label="运行环境">
                <li v-for="(pill, i) in $tm('home.envPills')" :key="i">
                  <i class="bi bi-check2-circle" aria-hidden="true"></i>{{ pill }}
                </li>
              </ul>
            </div>
          </div>
          <div class="col-lg-6">
            <div class="hero-image-wrapper fade-in">
              <div class="decorative-circle" aria-hidden="true"></div>
              <div class="decorative-dot dot-1" aria-hidden="true"></div>
              <div class="decorative-dot dot-2" aria-hidden="true"></div>
              <img 
                src="@/assets/home.png" 
                alt="NFC Share App Interface" 
                class="hero-img"
                loading="eager"
              >
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="features-section section-block" aria-labelledby="features-title">
      <div class="container">
        <div class="section-head text-center reveal">
          <h2 id="features-title" class="section-title">{{ $t('home.featuresTitle') }}</h2>
          <p class="section-subtitle">{{ $t('home.featuresSubtitle') }}</p>
        </div>
        <div class="row g-4">
          <div class="col-md-4" v-for="f in featureList" :key="f.key">
            <article class="feature-card reveal">
              <div class="feature-icon" :aria-label="f.key + ' icon'">
                <i class="bi" :class="f.icon"></i>
              </div>
              <h3>{{ $t(`home.features.${f.key}.title`) }}</h3>
              <p class="card-tag">{{ $t(`home.features.${f.key}.subtitle`) }}</p>
              <p class="card-desc">{{ $t(`home.features.${f.key}.description`) }}</p>
              <ul class="list-unstyled">
                <li v-for="n in 3" :key="n">
                  <i class="bi bi-check-circle-fill" aria-hidden="true"></i>
                  {{ $t(`home.features.${f.key}.items.item${n}`) }}
                </li>
              </ul>
            </article>
          </div>
        </div>
      </div>
    </section>

    <!-- Showcase Section -->
    <section class="showcase-section section-block" aria-labelledby="showcase-title">
      <div class="container">
        <div class="section-head text-center reveal">
          <h2 id="showcase-title" class="section-title">{{ $t('home.showcase.title') }}</h2>
          <p class="section-subtitle">{{ $t('home.showcase.subtitle') }}</p>
        </div>
        <div class="row g-4">
          <div class="col-6 col-lg-3" v-for="(item, i) in showcaseList" :key="i">
            <figure class="shot-card reveal">
              <img :src="item.img" :alt="item.title" loading="lazy">
              <figcaption>
                <strong>{{ item.title }}</strong>
                <span>{{ item.desc }}</span>
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
    </section>

    <!-- Steps Section -->
    <section class="steps-section section-block" aria-labelledby="steps-title">
      <div class="container">
        <div class="section-head text-center reveal">
          <h2 id="steps-title" class="section-title">{{ $t('home.steps.title') }}</h2>
          <p class="section-subtitle">{{ $t('home.steps.subtitle') }}</p>
        </div>
        <div class="row g-4">
          <div class="col-6 col-lg-3" v-for="(step, i) in $tm('home.steps.items')" :key="i">
            <div class="step-item reveal">
              <span class="step-num">{{ i + 1 }}</span>
              <h3>{{ step.title }}</h3>
              <p>{{ step.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Scenes Section -->
    <section class="scenes-section section-block" aria-labelledby="scenes-title">
      <div class="container">
        <div class="section-head text-center reveal">
          <h2 id="scenes-title" class="section-title">{{ $t('home.scenes.title') }}</h2>
          <p class="section-subtitle">{{ $t('home.scenes.subtitle') }}</p>
        </div>
        <div class="row g-3">
          <div class="col-6 col-lg-3" v-for="(scene, i) in scenesList" :key="i">
            <div class="scene-card reveal">
              <i class="bi" :class="scene.icon" aria-hidden="true"></i>
              <h3>{{ scene.title }}</h3>
              <p>{{ scene.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import shotMedia from '@/assets/screens/shot-media.jpg'
import shotBasic from '@/assets/screens/shot-basic.jpg'
import shotUid from '@/assets/screens/shot-uid.jpg'
import shotPurchase from '@/assets/screens/shot-purchase.jpg'

export default {
  name: 'Home',
  data() {
    return {
      observer: null,
      featureList: [
        { key: 'uidReader', icon: 'bi-qr-code-scan' },
        { key: 'freeWrite', icon: 'bi-pencil-square' },
        { key: 'advancedWrite', icon: 'bi-camera-video' }
      ]
    }
  },
  computed: {
    showcaseList() {
      const imgs = [shotMedia, shotBasic, shotUid, shotPurchase]
      return this.$tm('home.showcase.items').map((item, i) => ({
        ...item,
        img: imgs[i]
      }))
    },
    scenesList() {
      const icons = ['bi-box-seam', 'bi-person-badge', 'bi-shop', 'bi-megaphone']
      return this.$tm('home.scenes.items').map((item, i) => ({
        ...item,
        icon: icons[i]
      }))
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initScrollAnimations()
    })
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  methods: {
    initScrollAnimations() {
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible')
            this.observer.unobserve(entry.target)
          }
        })
      }, observerOptions)

      document.querySelectorAll('.reveal').forEach(el => {
        this.observer.observe(el)
      })
    },
  }
}
</script>

<style scoped>
.home-page {
  padding-top: 72px;
}

/* 收紧内容宽度，避免宽屏下散得太开 */
.hero-section .container,
.section-block .container {
  max-width: 1120px !important;
  margin-left: auto;
  margin-right: auto;
}

/* ==================== Hero Section ==================== */
.hero-section {
  background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 50%, #f8f9fa 100%);
  color: var(--color-text-primary);
  position: relative;
  overflow: hidden;
  padding: clamp(2.5rem, 7vh, 5rem) 0;
  display: flex;
  align-items: center;
}

.hero-content {
  position: relative;
  z-index: 1;
  padding-right: clamp(1rem, 3vw, 3rem);
}

.hero-title {
  font-size: clamp(32px, 4vw, 56px);
  line-height: 1.18;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--color-text-primary);
  margin-bottom: 1.25rem;
  overflow-wrap: break-word;
  text-wrap: balance;
}

.hero-description {
  font-size: clamp(16px, 1.35vw, 19px);
  line-height: 1.8;
  font-weight: 400;
  letter-spacing: 0.01em;
  color: var(--color-text-secondary);
  margin-bottom: 1.75rem;
  max-width: 520px;
}

/* 环境标签 */
.env-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 0;
  margin: 1.75rem 0 0;
  list-style: none;
}

.env-pills li {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-secondary);
  background: rgba(0, 113, 227, 0.06);
  border: 1px solid rgba(0, 113, 227, 0.12);
  padding: 6px 14px;
  border-radius: 980px;
  white-space: nowrap;
}

.env-pills .bi {
  color: var(--color-primary);
  font-size: 14px;
}

/* Hero Image */
.hero-image-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 0;
}

.decorative-circle {
  position: absolute;
  width: min(400px, 80%);
  aspect-ratio: 1;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  opacity: 0.14;
  z-index: 1;
  animation: float 6s ease-in-out infinite;
  will-change: transform;
}

.decorative-dot {
  position: absolute;
  border-radius: 50%;
  z-index: 1;
  will-change: transform;
}

.dot-1 {
  top: 10%;
  right: 12%;
  width: 16px;
  height: 16px;
  background: var(--color-primary);
  animation: float 4s ease-in-out infinite;
}

.dot-2 {
  bottom: 12%;
  left: 5%;
  width: 12px;
  height: 12px;
  background: #667eea;
  animation: float 5s ease-in-out infinite 1s;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-16px);
  }
}

.hero-img {
  width: auto;
  max-width: 100%;
  max-height: min(600px, 68vh);
  height: auto;
  position: relative;
  z-index: 2;
  filter: drop-shadow(0 32px 64px rgba(0, 0, 0, 0.12));
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
}

.hero-img:hover {
  transform: scale(1.02) translateY(-6px);
}

/* Hero Animations */
.fade-in-up {
  animation: fadeInUp 0.8s ease-out;
}

.fade-in {
  animation: fadeIn 1s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Download Buttons */
.hero-buttons {
  display: flex;
  gap: clamp(0.75rem, 1.5vw, 1.25rem);
  flex-wrap: wrap;
  align-items: center;
}

.download-btn {
  display: inline-block;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  will-change: transform;
}

.download-btn:hover {
  transform: translateY(-3px);
}

.download-btn:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 4px;
  border-radius: 8px;
}

.download-btn img {
  height: clamp(42px, 3.4vw, 50px);
  width: auto;
  display: block;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.06));
  transition: filter 0.3s ease;
}

.download-btn:hover img {
  filter: drop-shadow(0 8px 20px rgba(0, 0, 0, 0.1));
}

.download-btn-wrapper {
  position: relative;
  display: inline-block;
}

.download-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.download-btn.disabled:hover {
  transform: none;
}

.coming-soon-badge {
  position: absolute;
  top: -10px;
  right: -10px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 4px 11px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  z-index: 2;
  white-space: nowrap;
  letter-spacing: 0.02em;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

/* ==================== 通用板块 ==================== */
.section-block {
  padding: clamp(2.75rem, 6vh, 4.5rem) 0;
}

.section-head {
  margin-bottom: clamp(2rem, 4vh, 3rem);
}

.section-title {
  font-size: clamp(26px, 2.8vw, 36px);
  line-height: 1.2;
  font-weight: 700;
  letter-spacing: -0.01em;
  margin-bottom: 0.625rem;
}

.section-subtitle {
  font-size: clamp(15px, 1.3vw, 17px);
  line-height: 1.6;
  font-weight: 400;
  color: var(--color-text-secondary);
  margin-bottom: 0;
}

/* 滚动显现 */
.reveal {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.55s ease, transform 0.55s ease;
}

.reveal.visible {
  opacity: 1;
  transform: translateY(0);
}

/* ==================== 核心功能 ==================== */
.features-section {
  background: var(--color-background);
}

.feature-card {
  background: var(--color-surface);
  border-radius: var(--radius-lg);
  padding: clamp(1.5rem, 2.2vw, 2rem);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
  transition: box-shadow 0.3s ease, transform 0.55s ease, opacity 0.55s ease;
  height: 100%;
  border: 1px solid rgba(0, 0, 0, 0.04);
}

.feature-card.visible:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.08);
}

.feature-card h3 {
  font-size: 20px;
  line-height: 1.3;
  font-weight: 600;
  margin-top: 1.125rem;
  margin-bottom: 0.375rem;
}

.feature-card .card-tag {
  color: var(--color-primary);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 0.625rem;
}

.feature-card .card-desc {
  font-size: 15px;
  line-height: 1.7;
  color: var(--color-text-secondary);
  margin-bottom: 0.875rem;
}

.feature-card ul li {
  font-size: 14px;
  line-height: 1.6;
  color: var(--color-text-secondary);
  margin-bottom: 6px;
}

.feature-card ul li .bi {
  color: #34c759;
  margin-right: 6px;
}

.feature-icon {
  width: 52px;
  height: 52px;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-hover) 100%);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  color: white;
}

/* ==================== 功能实拍 ==================== */
.showcase-section {
  background: linear-gradient(180deg, #f5f7fa 0%, #ffffff 100%);
}

.shot-card {
  margin: 0;
  background: var(--color-surface);
  border-radius: var(--radius-lg);
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.3s ease, transform 0.55s ease, opacity 0.55s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.shot-card.visible:hover {
  transform: translateY(-6px);
  box-shadow: 0 14px 36px rgba(0, 0, 0, 0.1);
}

.shot-card img {
  width: 100%;
  height: auto;
  display: block;
  aspect-ratio: 640 / 1385;
  object-fit: cover;
  object-position: top;
}

.shot-card figcaption {
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.shot-card figcaption strong {
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.shot-card figcaption span {
  font-size: 13px;
  line-height: 1.55;
  color: var(--color-text-secondary);
}

/* ==================== 使用步骤 ==================== */
.steps-section {
  background: var(--color-background);
}

.step-item {
  text-align: center;
  padding: 1.5rem 1rem;
  height: 100%;
}

.step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-hover) 100%);
  color: white;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 1rem;
  box-shadow: 0 6px 16px rgba(0, 113, 227, 0.25);
}

.step-item h3 {
  font-size: 17px;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.step-item p {
  font-size: 14px;
  line-height: 1.65;
  color: var(--color-text-secondary);
  margin-bottom: 0;
}

/* ==================== 适用场景 ==================== */
.scenes-section {
  background: linear-gradient(180deg, #ffffff 0%, #f5f7fa 100%);
}

.scene-card {
  background: var(--color-surface);
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: var(--radius-md);
  padding: 1.375rem 1.25rem;
  height: 100%;
  transition: box-shadow 0.3s ease, transform 0.55s ease, opacity 0.55s ease;
}

.scene-card.visible:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.07);
}

.scene-card .bi {
  font-size: 1.5rem;
  color: var(--color-primary);
  display: block;
  margin-bottom: 0.75rem;
}

.scene-card h3 {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 0.375rem;
}

.scene-card p {
  font-size: 13px;
  line-height: 1.6;
  color: var(--color-text-secondary);
  margin-bottom: 0;
}

/* ==================== Responsive ==================== */
@media (max-width: 992px) {
  .hero-content {
    padding-right: 0;
    margin-bottom: 2.5rem;
    text-align: center;
  }

  .hero-title {
    font-size: clamp(30px, 5.5vw, 40px);
  }

  .hero-description {
    margin-left: auto;
    margin-right: auto;
  }

  .hero-buttons,
  .env-pills {
    justify-content: center;
  }

  .hero-img {
    max-height: 520px;
  }

  .decorative-circle {
    width: min(300px, 70%);
  }
}

@media (max-width: 734px) {
  .home-page {
    padding-top: 64px;
  }

  .hero-section {
    padding: 2.25rem 0;
  }

  .hero-title {
    font-size: 28px;
    margin-bottom: 1rem;
  }

  .hero-description {
    font-size: 16px;
    margin-bottom: 1.5rem;
  }

  .hero-image-wrapper {
    padding: 0.5rem 0;
  }

  .hero-img {
    max-height: 420px;
  }

  .decorative-circle {
    width: min(240px, 70%);
  }

  .download-btn img {
    height: 42px;
  }

  .coming-soon-badge {
    font-size: 11px;
    padding: 3px 9px;
    top: -8px;
    right: -8px;
  }

  .env-pills li {
    font-size: 12px;
    padding: 5px 12px;
  }

  .section-block {
    padding: 2.5rem 0;
  }

  .step-item {
    padding: 1rem 0.5rem;
  }
}

/* Reduced Motion Support */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }

  .reveal {
    opacity: 1;
    transform: none;
  }
}

/* Dark Mode Support */
@media (prefers-color-scheme: dark) {
  .home-page {
    --color-text-primary: #f5f5f7;
    --color-text-secondary: #a1a1a6;
    --color-background: #000000;
    --color-surface: #1d1d1f;
  }

  .hero-section {
    background: linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #0a0a0a 100%);
  }

  .showcase-section,
  .scenes-section {
    background: #0a0a0a;
  }

  .feature-card,
  .shot-card,
  .scene-card {
    border-color: rgba(255, 255, 255, 0.1);
  }
}
</style>
